import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MarketRecap } from "../../features/kquant/types";
import { Button, Panel } from "../../components/ui/primitives";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

type FinanceCollectionApi = { label: string; sector_id: string };

type AnalyzeStage = "idle" | "fetching_context" | "generating";

type AnalyzeMetaFrame = { type: "meta"; session_id: string };

type AnalyzeStepFrame = { type: "step"; stage: "fetching_context" | "generating" };

type AnalyzeContentFrame = { content: string };

type AnalyzeErrorFrame = { error: string };

type AnalyzeThinkingFrame = { type: "thinking"; content: string };

type AnalyzeFrame = AnalyzeMetaFrame | AnalyzeStepFrame | AnalyzeContentFrame | AnalyzeErrorFrame | AnalyzeThinkingFrame;

function extractSseMessages(buffer: string) {
  const chunks = buffer.split("\n\n");
  if (chunks.length <= 1) return { messages: [] as string[], rest: buffer };
  const rest = chunks[chunks.length - 1] ?? "";
  const messages = chunks
    .slice(0, -1)
    .map((x) => x.trim())
    .filter(Boolean);
  return { messages, rest };
}

function parseSseDataLines(message: string) {
  const lines = message.split("\n");
  const dataLines: string[] = [];
  for (const ln of lines) {
    const trimmed = ln.trim();
    if (!trimmed) continue;
    if (trimmed.startsWith("data:")) {
      dataLines.push(trimmed.slice("data:".length).trimStart());
    }
  }
  return dataLines.join("\n");
}

function MarkdownRenderer({
  content,
  theme = "dark",
}: {
  content: string;
  theme?: "light" | "dark";
}) {
  if (!content) return null;
  const isDark = theme === "dark";
  return (
    <div className="space-y-3">
      <ReactMarkdown
        remarkPlugins={[remarkBreaks, remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h1 className={`mt-6 text-lg font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className={`mt-5 text-base font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className={`mt-4 text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              {children}
            </h3>
          ),
          h4: ({ children }) => (
            <h4 className={`mt-4 text-xs font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              {children}
            </h4>
          ),
          p: ({ children }) => <p className={isDark ? "text-white/70" : "text-slate-600"}>{children}</p>,
          strong: ({ children }) => (
            <strong className={`font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{children}</strong>
          ),
          hr: () => <hr className={`my-4 ${isDark ? "border-white/10" : "border-slate-200"}`} />,
          br: () => <br />,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

function formatThinkingTime(ms: number) {
  if (ms < 0) ms = 0;
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const milliseconds = ms % 1000;
  return `${minutes}:${seconds.toString().padStart(2, "0")}.${milliseconds.toString().padStart(3, "0")}`;
}

export function MarketRecapSection({
  recap,
  theme = "dark",
}: {
  recap: MarketRecap | null;
  theme?: "light" | "dark";
}) {
  const isDark = theme === "dark";
  const [phase, setPhase] = useState<"idle" | "spinning" | "streaming">("idle");
  const [streamedText, setStreamedText] = useState<string>("");
  const [sectors, setSectors] = useState<FinanceCollectionApi[]>([]);
  const [selectedSectorId, setSelectedSectorId] = useState<string>("");

  const [thinkingText, setThinkingText] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  const [thinkingElapsedMs, setThinkingElapsedMs] = useState(0);
  const [thinkingOpen, setThinkingOpen] = useState(false);
  const thinkingUserToggledRef = useRef(false);

  const abortRef = useRef<AbortController | null>(null);
  const streamSeqRef = useRef(0);
  const stageRef = useRef<AnalyzeStage>("idle");
  const sessionIdRef = useRef<string | null>(null);
  const thinkingStartTimestampRef = useRef<number | null>(null);

  const spinTimeoutRef = useRef<number | null>(null);
  const streamTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    fetch("/api/v1/finance/collections?context=news")
      .then((res) => res.json())
      .then((data: FinanceCollectionApi[]) => {
        setSectors(data);

        setSelectedSectorId((prev) => {
          if (prev) return prev;
          const firstId = data?.[0]?.sector_id;
          return typeof firstId === "string" ? firstId : "";
        });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let interval: number;
    if (isThinking) {
      interval = window.setInterval(() => {
        if (thinkingStartTimestampRef.current != null) {
          setThinkingElapsedMs(Date.now() - thinkingStartTimestampRef.current);
        }
      }, 50);
    }
    return () => {
      window.clearInterval(interval);
    };
  }, [isThinking]);

  const clearTimers = useCallback(() => {
    if (spinTimeoutRef.current != null) {
      window.clearTimeout(spinTimeoutRef.current);
      spinTimeoutRef.current = null;
    }
    if (streamTimeoutRef.current != null) {
      window.clearTimeout(streamTimeoutRef.current);
      streamTimeoutRef.current = null;
    }
  }, []);

  const stopStreaming = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    clearTimers();
  }, [clearTimers]);

  useEffect(() => {
    return () => {
      stopStreaming();
    };
  }, [stopStreaming]);

  const isGenerating = phase !== "idle";

  const onGenerate = useCallback(() => {
    if (isGenerating) return;

    stopStreaming();

    const requestId = ++streamSeqRef.current;
    const controller = new AbortController();
    abortRef.current = controller;

    setPhase("spinning");
    stageRef.current = "idle";
    sessionIdRef.current = null;
    setStreamedText("");
    
    setThinkingText("");
    setIsThinking(false);
    setThinkingElapsedMs(0);
    thinkingStartTimestampRef.current = null;
    setThinkingOpen(false);
    thinkingUserToggledRef.current = false;

    const query = "Give me today's U.S. market recap with key drivers across sectors and themes.";

    void (async () => {
      let localIsThinking = false;
      try {
        const res = await fetch("/api/v1/finance/market-analyze", {
          method: "POST",
          headers: {
            Accept: "text/event-stream",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            query,
            context_config: {
              include_news: true,
              include_sentiment: true,
              include_heatmap: false,
              sector_id: selectedSectorId || undefined,
            },
          }),
          signal: controller.signal,
        });

        if (!res.ok) {
          const body = await res.text().catch(() => "");
          throw new Error(body || `${res.status} ${res.statusText}`);
        }

        if (!res.body) {
          throw new Error("Missing response body.");
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();

        let buffer = "";
        let sawMeta = false;
        let assistantBuffer = "";
        let localThinkingText = "";

        while (true) {
          const { done, value } = await reader.read();
          if (controller.signal.aborted || requestId !== streamSeqRef.current) return;

          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const extracted = extractSseMessages(buffer);
          buffer = extracted.rest;

          for (const msg of extracted.messages) {
            const data = parseSseDataLines(msg);
            if (!data) continue;

            if (data === "[DONE]") {
              if (localIsThinking) {
                localIsThinking = false;
                setIsThinking(false);
                if (thinkingStartTimestampRef.current) {
                  setThinkingElapsedMs(Date.now() - thinkingStartTimestampRef.current);
                }
                if (!thinkingUserToggledRef.current) {
                  setThinkingOpen(false);
                }
              }
              stageRef.current = "idle";
              setPhase("idle");
              return;
            }

            let payload: AnalyzeFrame;
            try {
              payload = JSON.parse(data) as AnalyzeFrame;
            } catch {
              continue;
            }

            if (!sawMeta) {
              if ((payload as any)?.type !== "meta" || typeof (payload as any)?.session_id !== "string") {
                throw new Error("Invalid SSE protocol: first frame must be meta.");
              }
              const meta = payload as AnalyzeMetaFrame;
              sawMeta = true;
              sessionIdRef.current = meta.session_id;
              continue;
            }

            if ((payload as any)?.type === "step") {
              const step = payload as AnalyzeStepFrame;
              if (step.stage === "fetching_context") {
                stageRef.current = "fetching_context";
              } else if (step.stage === "generating") {
                stageRef.current = "generating";
                setPhase("streaming");
              }
              continue;
            }

            if (typeof (payload as any)?.error === "string") {
              if (localIsThinking) {
                localIsThinking = false;
                setIsThinking(false);
                if (thinkingStartTimestampRef.current) {
                  setThinkingElapsedMs(Date.now() - thinkingStartTimestampRef.current);
                }
                if (!thinkingUserToggledRef.current) {
                  setThinkingOpen(false);
                }
              }
              setStreamedText(String((payload as any).error));
              stageRef.current = "idle";
              setPhase("idle");
              return;
            }

            const p = payload as any;

            if (p.type === "thinking" && typeof p.content === "string") {
              if (!localIsThinking) {
                localIsThinking = true;
                setIsThinking(true);
                thinkingStartTimestampRef.current = Date.now();
                if (!thinkingUserToggledRef.current) {
                  setThinkingOpen(true);
                }
              }
              localThinkingText += p.content;
              setThinkingText(localThinkingText);
              continue;
            }

            if (typeof p.content === "string") {
              if (localIsThinking) {
                localIsThinking = false;
                setIsThinking(false);
                if (thinkingStartTimestampRef.current) {
                  setThinkingElapsedMs(Date.now() - thinkingStartTimestampRef.current);
                }
                if (!thinkingUserToggledRef.current) {
                  setThinkingOpen(false);
                }
              }
              assistantBuffer += (payload as AnalyzeContentFrame).content;
              setStreamedText(assistantBuffer);
            }
          }
        }

        if (localIsThinking) {
          localIsThinking = false;
          setIsThinking(false);
          if (thinkingStartTimestampRef.current) {
            setThinkingElapsedMs(Date.now() - thinkingStartTimestampRef.current);
          }
          if (!thinkingUserToggledRef.current) {
            setThinkingOpen(false);
          }
        }
        stageRef.current = "idle";
        setPhase("idle");
      } catch (err) {
        if (controller.signal.aborted || requestId !== streamSeqRef.current) return;
        if (localIsThinking) {
          localIsThinking = false;
          setIsThinking(false);
          if (thinkingStartTimestampRef.current) {
            setThinkingElapsedMs(Date.now() - thinkingStartTimestampRef.current);
          }
          if (!thinkingUserToggledRef.current) {
            setThinkingOpen(false);
          }
        }
        const message = err instanceof Error ? err.message : "Failed to generate.";
        setStreamedText(message);
        stageRef.current = "idle";
        setPhase("idle");
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
      }
    })();
  }, [isGenerating, stopStreaming, selectedSectorId]);

  const displayText = useMemo(() => {
    if (phase === "idle") {
      if (streamedText.trim()) return streamedText;
      return recap?.narrative ?? "Loading…";
    }
    return streamedText;
  }, [phase, recap?.narrative, streamedText]);

  return (
    <section className="mt-7">
      <Panel className={"p-6 " + (isDark ? "" : "border-slate-200 bg-white shadow-sm")}>
        <div className="flex items-baseline gap-4">
          <div className="text-sm font-semibold">U.S. Market Recap</div>
          <div className={"text-xs " + (isDark ? "text-white/55" : "text-slate-500")}>
            Daily Market Trends
          </div>
        </div>

        <div className="mt-4">
          <div className={"mb-2 text-xs font-semibold " + (isDark ? "text-white/65" : "text-slate-600")}>
            Sector
          </div>
          <select
            className={
              "kq-select h-9 w-[200px] rounded-xl border px-3 text-xs outline-none " +
              (isDark
                ? "border-white/10 bg-white/5 text-white/80"
                : "border-slate-200 bg-white text-slate-700")
            }
            value={selectedSectorId}
            onChange={(e) => setSelectedSectorId(e.target.value)}
          >
            {sectors.map((s) => (
              <option key={s.sector_id} value={s.sector_id}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-4">
          <Button
            variant="primary"
            size="sm"
            className="rounded-full"
            onClick={onGenerate}
            disabled={isGenerating}
          >
            {phase === "spinning" ? (
              <>
                <span
                  className={
                    "h-3.5 w-3.5 animate-spin rounded-full border-2 " +
                    (isDark
                      ? "border-white/25 border-t-white/80"
                      : "border-slate-300 border-t-slate-700")
                  }
                  aria-hidden
                />
                Generating…
              </>
            ) : (
              "Generate U.S. Market Analysis"
            )}
          </Button>
        </div>

        <div
          className={
            "mt-4 rounded-2xl border p-5 text-xs leading-relaxed " +
            (isDark
              ? "border-white/10 bg-white/5 text-white/70"
              : "border-slate-200 bg-slate-50 text-slate-700")
          }
        >
          {(thinkingText || isThinking) && (
            <div
              className={
                "mb-4 overflow-hidden rounded-xl border " +
                (isDark
                  ? "border-white/10 bg-white/5"
                  : "border-slate-200 bg-slate-50")
              }
            >
              <button
                onClick={() => {
                  thinkingUserToggledRef.current = true;
                  setThinkingOpen((o) => !o);
                }}
                className={
                  "flex w-full items-center justify-between px-4 py-2 text-xs font-medium transition-colors " +
                  (isDark
                    ? "text-white/60 hover:bg-white/5"
                    : "text-slate-600 hover:bg-slate-100")
                }
              >
                <div className="flex items-center gap-2">
                  <span>Thinking Process</span>
                  {isThinking && <span className="animate-pulse">...</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono opacity-80">
                    {formatThinkingTime(thinkingElapsedMs)}
                  </span>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`transform transition-transform duration-200 ${thinkingOpen ? "rotate-180" : ""}`}
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
              </button>
              <div
                className={`grid transition-[grid-template-rows] duration-200 ease-out ${
                  thinkingOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                }`}
              >
                <div className="overflow-hidden">
                  <div
                    className={
                      "border-t px-4 pb-3 pt-3 " +
                      (isDark
                        ? "border-white/10 bg-black/10"
                        : "border-slate-200 bg-slate-50")
                    }
                  >
                    <MarkdownRenderer content={thinkingText} theme={theme} />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <MarkdownRenderer content={displayText} theme={theme} />
          {phase === "streaming" ? (
            <span className={"ml-0.5 inline-block w-2 animate-pulse " + (isDark ? "text-white/70" : "text-slate-600")}>
              ▍
            </span>
          ) : null}
        </div>
      </Panel>
    </section>
  );
}
